import { Cookies } from 'quasar'
import { getRequestContext } from '../../base/store/storeContext'

/**
 * Replaces the `store.$cookies` monkey-patch each app's container.ts applied.
 *
 * On the server a cookie jar has to be parsed out of the request, so this resolves per request
 * through the same context the stores use — a module-level jar would read one visitor's cookies
 * while rendering another's page. In the browser Quasar's `Cookies` plugin talks to
 * `document.cookie` directly and needs no scoping.
 *
 * Deliberately not a wrapper around all of Cookies: only what the auth flow needs.
 */

const cookieJar = () => {
  if (process.env.SERVER) {
    const context = getRequestContext()

    if (!context.cookies) {
      context.cookies = Cookies.parseSSR(context.ssrContext)
    }

    return context.cookies
  }

  return Cookies
}

export const useCookies = () => {
  const get = <T = string> (name: string): T => {
    return cookieJar().get(name)
  }

  const set = (name: string, value: string, options?: Record<string, unknown>) => {
    cookieJar().set(name, value, options)
  }

  const has = (name: string): boolean => {
    return cookieJar().has(name)
  }

  const remove = (name: string, options?: Record<string, unknown>) => {
    cookieJar().remove(name, options)
  }

  return {
    cookies: cookieJar,
    get,
    set,
    has,
    remove
  }
}
